import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import type { SpendingMixRow } from '../mock/financial-snapshot-compute';

@Component({
  selector: 'bn-financial-snapshot-spending-mix',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CurrencyPipe, DecimalPipe],
  template: `
    <div class="bn-fs-mix">
      <div class="bn-fs-mix__heading-row">
        <h3 class="bn-fs-mix__heading">Spending Mix</h3>
        <span class="bn-fs-mix__heading-meta">% of income</span>
      </div>
      <ul class="bn-fs-mix__list">
        @for (row of rows(); track row.key) {
          <li class="bn-fs-mix__item">
            <div class="bn-fs-mix__meta">
              @if (row.expandable) {
                <button
                  type="button"
                  class="bn-fs-mix__label bn-fs-mix__label--btn"
                  [attr.aria-expanded]="isExpanded(row.key)"
                  (click)="toggle(row.key)"
                >
                  <i
                    class="pi bn-fs-mix__chev"
                    [class.pi-chevron-up]="isExpanded(row.key)"
                    [class.pi-chevron-right]="!isExpanded(row.key)"
                    aria-hidden="true"
                  ></i>
                  {{ row.label }}
                </button>
              } @else {
                <span class="bn-fs-mix__label">{{ row.label }}</span>
              }
              <span class="bn-fs-mix__values">
                @if (row.amount === null) {
                  —
                } @else {
                  {{ row.amount | currency: 'USD' : 'symbol' : '1.0-0' }}
                }
                @if (row.percentOfIncome !== null) {
                  <span class="bn-fs-mix__pct">{{ row.percentOfIncome | number: '1.0-0' }}%</span>
                }
              </span>
            </div>
            <div class="bn-fs-mix__bar-track" role="presentation">
              <div class="bn-fs-mix__bar-fill" [style.width.%]="barWidth(row)"></div>
            </div>
            @if (row.expandable && isExpanded(row.key) && row.subItems.length) {
              <ul class="bn-fs-mix__subs">
                @for (sub of row.subItems; track sub.key) {
                  <li class="bn-fs-mix__sub">
                    <span>{{ sub.label }}</span>
                    <span>
                      @if (sub.amount === null) {
                        —
                      } @else {
                        {{ sub.amount | currency: 'USD' : 'symbol' : '1.0-0' }}
                      }
                    </span>
                  </li>
                }
              </ul>
            }
          </li>
        }
      </ul>
    </div>
  `,
  styleUrl: './spending-mix.component.scss',
})
export class FinancialSnapshotSpendingMixComponent {
  readonly rows = input.required<SpendingMixRow[]>();
  private readonly _expanded = signal<Record<string, boolean>>({});

  isExpanded(key: string): boolean {
    return !!this._expanded()[key];
  }

  toggle(key: string): void {
    this._expanded.update((m) => ({ ...m, [key]: !m[key] }));
  }

  /** Bar width = % of income (capped at 100), not normalized to the largest category. */
  barWidth(row: SpendingMixRow): number {
    if (row.percentOfIncome === null || row.percentOfIncome <= 0) {
      return 0;
    }
    return Math.min(100, row.percentOfIncome);
  }
}
